/**
 * AdminResources — Phase A-4.
 * Page unique avec 3 onglets : Stockage / LLM / Performance.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  HardDrive, Cpu, Gauge, RefreshCw, Trash2, FileText, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/components/admin/RoleGuard";
import {
  fetchStorageStats, fetchStorageFiles, deleteStorageFile,
  fetchLlmStats, fetchLlmLogs, fetchPerformance,
} from "@/lib/admin-api";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, n = Number(b) || 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i ? 1 : 0)} ${u[i]}`;
};
const fmtNum = (n) => (n ?? 0).toLocaleString("fr-FR");
const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR") : "—";

function KpiCard({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const tones = {
    default: "bg-muted/30",
    warn: "bg-amber-50 dark:bg-amber-950/20 border-amber-300/60",
    bad: "bg-red-50 dark:bg-red-950/20 border-red-300/60",
    good: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300/60",
  };
  return (
    <div
      className={`rounded-lg border p-4 ${tones[tone]}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-bold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function AdminResources() {
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");
  const [tab, setTab] = useState("storage");

  return (
    <div className="space-y-4" data-testid="admin-resources-page">
      <div className="flex items-center gap-2">
        <Gauge className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Ressources</h1>
        <span className="ml-auto text-xs text-muted-foreground">
          Monitoring infrastructure — lecture seule sauf suppression super_admin
        </span>
      </div>

      <div className="flex gap-2 border-b" data-testid="resources-tabs">
        {[
          ["storage", "Stockage", HardDrive],
          ["llm", "LLM", Cpu],
          ["perf", "Performance", Gauge],
        ].map(([k, l, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition ${
              tab === k
                ? "border-b-2 border-red-700 font-semibold text-red-700 dark:text-red-400"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`resources-tab-${k}`}
          >
            <Icon className="h-4 w-4" />
            {l}
          </button>
        ))}
      </div>

      {tab === "storage" && <StorageTab isSuper={isSuper} />}
      {tab === "llm" && <LlmTab />}
      {tab === "perf" && <PerformanceTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Storage                                                             */
/* ------------------------------------------------------------------ */
function StorageTab({ isSuper }) {
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        fetchStorageStats(),
        fetchStorageFiles({ page, limit }),
      ]);
      setStats(s);
      setFiles(f?.data || []);
      setTotal(f?.total || 0);
    } catch {
      toast.error("Erreur de chargement du stockage.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  async function onDelete(id, filename) {
    if (!isSuper) {
      toast.error("Seul un super_admin peut supprimer un fichier.");
      return;
    }
    const txt = prompt(
      `Supprimer définitivement le fichier "${filename}" ?\nTapez "SUPPRIMER" pour confirmer.`
    );
    if (txt !== "SUPPRIMER") return;
    try {
      await deleteStorageFile(id);
      toast.success("Fichier supprimé.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec.");
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={HardDrive}
          label="PDF stockés"
          value={fmtNum(stats?.pdf_count)}
          sub={fmtBytes(stats?.pdf_total_size)}
          testId="kpi-pdf"
        />
        <KpiCard
          icon={FileText}
          label="Rapports générés"
          value={fmtNum(stats?.reports_count)}
          testId="kpi-reports"
        />
        <KpiCard
          icon={HardDrive}
          label="Stockage MongoDB"
          value={fmtBytes(stats?.db_storage_size)}
          sub={`Données : ${fmtBytes(stats?.db_data_size)}`}
          testId="kpi-db"
        />
        <KpiCard
          icon={HardDrive}
          label="Total fichiers"
          value={fmtNum(total)}
          sub="Page courante"
          testId="kpi-files-total"
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Top 10 — Plus gros fichiers</CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="storage-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="top-files-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Fichier</th>
                <th className="px-3 py-2 text-right">Taille</th>
                <th className="px-3 py-2">Propriétaire</th>
                <th className="px-3 py-2">Uploadé</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.top_largest_files || []).map((f) => (
                <tr key={f.id} className="border-b">
                  <td className="px-3 py-2 font-mono">{f.filename || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtBytes(f.size)}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{f.owner_id || "—"}</td>
                  <td className="px-3 py-2">{fmtDateTime(f.uploaded_at)}</td>
                </tr>
              ))}
              {(!stats?.top_largest_files || stats.top_largest_files.length === 0) && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">Aucun fichier.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Tous les fichiers ({fmtNum(total)})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="files-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Fichier</th>
                <th className="px-3 py-2 text-right">Taille</th>
                <th className="px-3 py-2">Propriétaire</th>
                <th className="px-3 py-2">Projet</th>
                <th className="px-3 py-2">Uploadé</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} className="border-b" data-testid={`file-row-${f.id}`}>
                  <td className="px-3 py-2 font-mono">{f.filename || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtBytes(f.size)}</td>
                  <td className="px-3 py-2">{f.owner_email || f.owner_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{f.project_id || "—"}</td>
                  <td className="px-3 py-2">{fmtDateTime(f.uploaded_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(f.id, f.filename)}
                      disabled={!isSuper}
                      title={isSuper ? "Supprimer" : "Super_admin requis"}
                      data-testid={`file-delete-${f.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {files.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">Aucun fichier.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Précédent
        </Button>
        <span className="text-sm">Page {page} / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Suivant
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LLM                                                                 */
/* ------------------------------------------------------------------ */
function LlmTab() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetchLlmStats(),
        fetchLlmLogs({ page, limit }),
      ]);
      setStats(s);
      setLogs(l?.data || []);
      setTotal(l?.total || 0);
    } catch {
      toast.error("Erreur de chargement LLM.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const errorsTone = (stats?.errors_month || 0) > 10 ? "bad" : (stats?.errors_month || 0) > 0 ? "warn" : "good";
  const cacheTone = (stats?.cache_rate_pct || 0) >= 30 ? "good" : "default";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard icon={Cpu} label="Appels aujourd'hui" value={fmtNum(stats?.calls_today)} testId="kpi-llm-today" />
        <KpiCard icon={Cpu} label="Appels ce mois" value={fmtNum(stats?.calls_month)} testId="kpi-llm-month" />
        <KpiCard
          icon={Cpu}
          label="Cache hits (mois)"
          value={`${stats?.cache_rate_pct ?? 0}%`}
          sub={`${fmtNum(stats?.cache_hits_month)} hits`}
          tone={cacheTone}
          testId="kpi-llm-cache"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Erreurs (mois)"
          value={fmtNum(stats?.errors_month)}
          tone={errorsTone}
          testId="kpi-llm-errors"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Coût estimé</CardTitle>
        </CardHeader>
        <CardContent className="flex items-baseline gap-6">
          <div>
            <div className="text-[11px] uppercase text-muted-foreground">Ce mois</div>
            <div className="font-mono text-3xl font-bold">
              ${stats?.estimated_cost_usd_month ?? "0.00"}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Basé sur ${stats?.cost_per_call_usd ?? 0} / appel · {fmtNum(stats?.calls_month)} appels
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Journal des appels LLM ({fmtNum(total)})</CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="llm-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="llm-logs-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">Modèle</th>
                <th className="px-3 py-2 text-right">Tokens</th>
                <th className="px-3 py-2 text-right">Latence</th>
                <th className="px-3 py-2">Cache</th>
                <th className="px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((r, i) => (
                <tr key={i} className="border-b">
                  <td className="px-3 py-2 font-mono text-[10px]">{fmtDateTime(r.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">{r.user_id || r.user_email || "—"}</td>
                  <td className="px-3 py-2 font-mono">{r.model || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(r.tokens)}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.latency_ms ? `${r.latency_ms}ms` : "—"}</td>
                  <td className="px-3 py-2">{r.cached ? <Badge className="bg-emerald-600 text-white">HIT</Badge> : <Badge variant="outline">MISS</Badge>}</td>
                  <td className="px-3 py-2">
                    {r.status >= 400
                      ? <Badge className="bg-red-700 text-white">{r.status}</Badge>
                      : <Badge className="bg-emerald-600 text-white">{r.status || "OK"}</Badge>}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Aucun appel LLM journalisé.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {logs.length > 0 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Précédent
          </Button>
          <span className="text-sm">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Performance                                                         */
/* ------------------------------------------------------------------ */
function PerformanceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try { setData(await fetchPerformance()); }
    catch { toast.error("Erreur de chargement des performances."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const endpoints = data?.endpoints || [];
  const trend = data?.trend_7d || [];
  const maxTrend = Math.max(1, ...trend.map((t) => t.avg_latency_ms || 0));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Top 50 endpoints — 24 dernières heures</CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="perf-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="perf-endpoints-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Endpoint</th>
                <th className="px-3 py-2 text-right">Appels</th>
                <th className="px-3 py-2 text-right">Latence moy.</th>
                <th className="px-3 py-2 text-right">Latence max</th>
                <th className="px-3 py-2 text-right">Erreurs</th>
                <th className="px-3 py-2 text-right">% Erreur</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((e, i) => {
                const bad = e.error_rate_pct >= 5 || e.avg_latency_ms >= 2000;
                return (
                  <tr key={i} className={`border-b ${bad ? "bg-red-50/30 dark:bg-red-950/10" : ""}`}>
                    <td className="px-3 py-2 font-mono">{e.path}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(e.count)}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.avg_latency_ms}ms</td>
                    <td className="px-3 py-2 text-right font-mono">{e.max_latency_ms}ms</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(e.errors)}</td>
                    <td className="px-3 py-2 text-right">
                      {e.error_rate_pct >= 5
                        ? <Badge className="bg-red-700 text-white">{e.error_rate_pct}%</Badge>
                        : <span className="font-mono">{e.error_rate_pct}%</span>}
                    </td>
                  </tr>
                );
              })}
              {endpoints.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  Aucune donnée sur les 24 dernières heures.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Tendance latence — 7 jours</CardTitle>
        </CardHeader>
        <CardContent>
          {trend.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              Données insuffisantes pour tracer la tendance.
            </div>
          ) : (
            <div className="flex h-32 items-end gap-1" data-testid="perf-trend-chart">
              {trend.map((t) => {
                const h = Math.max(4, (t.avg_latency_ms / maxTrend) * 100);
                return (
                  <div key={t.date} className="flex flex-1 flex-col items-center gap-1">
                    <div className="text-[9px] font-mono text-muted-foreground">
                      {Math.round(t.avg_latency_ms)}ms
                    </div>
                    <div
                      className="w-full rounded-t bg-red-500/60 transition hover:bg-red-600"
                      style={{ height: `${h}%` }}
                      title={`${t.date} · ${t.count} appels`}
                    />
                    <div className="text-[9px] font-mono text-muted-foreground">
                      {t.date.slice(5)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
